import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, vdjLoop, VdjPost, VdjComfort, VDJ_DROP_FLASH, VDJ_MIRROR_CHUNK, VDJ_MIRROR_NAMES } from './lib/vdjLook.js';
import { loadArtworkTexture } from '../artwork-texture.js';

// Contrat du rig partagé : interdit. La ville a sa caméra et sa cible.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'cyberflight',
  family: 'geometric',
  danceability: 0.90,
  energyLevel: 0.86,
  bpmMin: 120,
  bpmMax: 175,
  movement: 0.90,
  tonality: 0.40,
  prefers: { gridStrength: [0.65, 1.0] },
  enabled: true
};

// Cyberflight : un vol rapide en rase-mottes au-dessus d'une ville infinie
// en fil de fer néon, la nuit. Les tours sont des colonnes d'égaliseur. LOOK
// VDJ, preset party (lib/vdjLook.js). Techno, trance, synthwave, drum'n'bass,
// électro.
//
// LA VILLE : grille de blocs d'une unité ; le bloc (bx, bz) porte une tour
// centrée en (bx + 0,5, bz + 0,5), d'emprise 0,56 au plus : les avenues sont
// les lignes entières x = n et z = n, larges de 0,44. Le contenu d'un bloc
// (vide, hauteur de base, 1 à 3 gradins, fenêtres, tour géante) vient d'un
// HACHAGE EXACT de ses coordonnées entières : la permutation mod 289 des
// bruits d'Ashima, dont tous les intermédiaires restent des entiers sous
// 2^24 - le CPU (JS) et le GPU (GLSL highp) calculent exactement les mêmes
// tours. C'est ce qui permet à la caméra d'éviter les tours : elle lit la
// ville sur le CPU. Période 289 blocs dans chaque axe.
//
// UN InstancedBufferGeometry de N x N tours (32 x 32 = 1 024 au tier 2),
// recyclé : l'instance (i, j) dessine le bloc b = base + mod(i - base, N) de
// la fenêtre courante, calculé dans le vertex shader depuis l'uniform de la
// fenêtre. Les positions sont RELATIVES à une origine entière proche de la
// caméra (précision après une heure de vol). Chaque tour = 3 boîtes (gradins)
// dont les inutilisées dégénèrent.
//
// FIL DE FER (R1) : les faces sont noires et opaques (elles masquent les
// tours de derrière) ; les ARÊTES sont la distance en pixels aux bords de la
// face, depuis ses UV (min(u, 1-u) / fwidth(u)) : cœur blanc 1,5 px (0,75 de
// chaque côté de l'arête), gaine de couleur jusqu'à 3 px, halo faible.
// Brouillard NOIR : multiplication, par la distance au centre de la fenêtre
// (aucune tour n'apparaît ni ne disparaît en vue) et à la caméra.
//
// L'ÉGALISEUR : 16 bandes par colonnes (bx mod 16), reconstruites depuis les
// trois enveloppes comme EqRig/Skyline (gaussiennes, décorrélation lente,
// attaque instantanée, chute linéaire, crête suspendue 0,8 s). Hauteur =
// base x (0,4 + 1,2 x niveau). Texture 16 x 1 (niveau, crête).
//
// LA CAMÉRA : caméra privée, rendu dans une cible privée 8 bits sRGB
// affichée par un quad (le miroir VDJ s'y applique). Vol : un bloc par
// temps (x2 puis x4 dans la montée). Plans coupés sur la mesure :
// AVENUE (au ras du sol au milieu d'une avenue), ROOFTOP et BANK (au-dessus
// du plus haut toit POSSIBLE - base x 1,6 - sur tout le trajet de la mesure),
// TOPDOWN (11 unités, au-dessus de la plus haute tour possible, 9,6), ORBIT
// (autour de la tour la plus haute du voisinage, au-dessus de toutes les
// autres dans l'anneau). La caméra ne traverse jamais une tour.
//
// Debug : ?cybershot=avenue|rooftop|bank|topdown|orbit, ?cybereq=<f>,
// ?cybermirror=none|h|quad|kaleido.

const TIER = {
  0: { n: 16, windows: false, cars: 96, samples: 0 },
  1: { n: 24, windows: true, cars: 160, samples: 0 },
  2: { n: 32, windows: true, cars: 224, samples: 4 }
};
const HALF = [0.28, 0.20, 0.13];            // half-footprint per tier
const EMPTY_P = 0.12, GIANT_P = 0.975;
const HEIGHT_MAX = 1.6;                    // 0.4 + 1.2 at level 1
const BASE_GIANT_MAX = 6.0;                // so the highest tower is 9.6
const TOPDOWN_Y = 11.0;
const CAM_CLEAR = 0.35;                    // above the highest possible roof
const ORBIT_R = 2.6;
const BANK_ROLL = 20 * Math.PI / 180;
const SIGN_SIZE = 0.45;
const SHOT_CYCLE = ['avenue', 'rooftop', 'avenue', 'bank', 'topdown', 'avenue', 'orbit', 'rooftop', 'bank', 'avenue'];
const SHOT_NAMES = ['avenue', 'rooftop', 'bank', 'topdown', 'orbit'];
// the equaliser
const BANDS = 16;
const BAND_CENTRES = [0.12, 0.50, 0.88];
const BAND_SIGMA = 0.26;
const DECORR = 0.10;
const FALL_PER_S = 1.6;
const PEAK_HOLD_S = 0.80;
const PEAK_FALL_PER_S = 0.5;               // the crest comes down SLOWLY
const KICK_SPIKE = 0.30;
const SYNTH_BPM = 128;
const MIRROR_EVERY_PHRASES = 4;            // a mirror one phrase in four (rarely)

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// --- the exact hash, shared by JS and GLSL --------------------------------------
// m289 rounds through (x + 0.5) / 289 so that a GPU division a few ulps off
// never moves an exact multiple to the wrong side of floor().
function m289(x) { return x - 289 * Math.floor((x + 0.5) / 289); }
function perm(x) { return m289((x * 34 + 1) * x); }
function hash(bx, bz, salt) { let p = perm(m289(bx) + salt); p = perm(p + m289(bz)); p = perm(p + 17); return p / 289; }
function blockProps(bx, bz) {
  const empty = hash(bx, bz, 0) < EMPTY_P;
  const r1 = hash(bx, bz, 31);
  const giant = hash(bx, bz, 67) > GIANT_P;
  const base = giant ? 4.5 + 1.5 * r1 : 0.55 + 2.2 * r1 * r1;
  const t = hash(bx, bz, 113);
  const tiers = giant ? 3 : (t < 0.55 ? 1 : (t < 0.85 ? 2 : 3));
  return { empty, base, giant, tiers };
}
const HASH_GLSL = `
  float m289(float x) { return x - 289.0 * floor((x + 0.5) / 289.0); }
  float perm(float x) { return m289((x * 34.0 + 1.0) * x); }
  float hsh(vec2 b, float salt) { float p = perm(m289(b.x) + salt); p = perm(p + m289(b.y)); p = perm(p + 17.0); return p / 289.0; }
  float imod(float a, float n) { return a - n * floor((a + 0.5) / n); }
`;

// --- shaders --------------------------------------------------------------------
const BLOCK_GLSL = `
  ${HASH_GLSL}
  uniform float uN;
  uniform vec2 uWinBase;          // absolute block of the window's corner
  uniform vec2 uOrigin;           // absolute block the relative frame starts at
  uniform vec2 uWinCentre;        // relative
  uniform sampler2D uLevels;      // 16 x 1: r level, g crest
  uniform float uFlat;            // the montee lowers every tower
  uniform float uSurge;           // the drop: every tower to its maximum
  uniform vec2 uFog;              // camera-distance fog: near, far
  // a block's content: x empty, y base height, z tiers, w giant
  vec4 blockOf(vec2 b) {
    float empty = step(hsh(b, 0.0), ${EMPTY_P.toFixed(3)});
    float r1 = hsh(b, 31.0);
    float giant = step(${GIANT_P.toFixed(3)}, hsh(b, 67.0));
    float base = giant > 0.5 ? 4.5 + 1.5 * r1 : 0.55 + 2.2 * r1 * r1;
    float t = hsh(b, 113.0);
    float tiers = giant > 0.5 ? 3.0 : (t < 0.55 ? 1.0 : (t < 0.85 ? 2.0 : 3.0));
    return vec4(empty, base, tiers, giant);
  }
  vec2 windowBlock(vec2 inst) { return uWinBase + vec2(imod(inst.x - uWinBase.x, uN), imod(inst.y - uWinBase.y, uN)); }
  vec2 levelsOf(vec2 b) {
    float band = imod(b.x, ${BANDS}.0);
    return texture2D(uLevels, vec2((band + 0.5) / ${BANDS}.0, 0.5)).rg;
  }
  float fogAt(vec3 p) {
    float dw = max(abs(p.x - uWinCentre.x), abs(p.z - uWinCentre.y)) / (uN * 0.5);
    float fw = 1.0 - smoothstep(0.68, 0.95, dw);
    float fc = 1.0 - smoothstep(uFog.x, uFog.y, length(p - cameraPosition));
    return fw * fc;
  }
`;

const towerVertex = `
  precision highp float;
  attribute vec2 aInst;
  attribute float aTier;
  attribute float aSideFace;
  ${BLOCK_GLSL}
  varying vec2 vUv;
  varying float vFog;
  varying float vLit;
  varying float vWin;
  varying vec2 vCells;
  varying float vSeed;
  uniform float uWindows;
  void main() {
    vec2 b = windowBlock(aInst);
    vec4 k = blockOf(b);
    vec2 lv = levelsOf(b);
    float level = max(lv.x, uSurge);
    float H = k.y * (0.4 + 1.2 * level) * uFlat;
    float tiers = k.z;
    // the tier's height range and footprint
    float y0 = 0.0, y1 = 1.0, hw = ${HALF[0].toFixed(2)};
    if (tiers > 2.5) {
      if (aTier < 0.5) { y0 = 0.0; y1 = 0.5; } else if (aTier < 1.5) { y0 = 0.5; y1 = 0.78; hw = ${HALF[1].toFixed(2)}; } else { y0 = 0.78; y1 = 1.0; hw = ${HALF[2].toFixed(2)}; }
    } else if (tiers > 1.5) {
      if (aTier < 0.5) { y0 = 0.0; y1 = 0.62; } else { y0 = 0.62; y1 = 1.0; hw = ${HALF[1].toFixed(2)}; }
    }
    float isOn = (1.0 - k.x) * step(aTier + 0.5, tiers);
    vec2 rel = b - uOrigin;
    vec3 p = vec3(rel.x + 0.5 + position.x * hw, (y0 + position.y * (y1 - y0)) * H, rel.y + 0.5 + position.z * hw);
    if (isOn < 0.5) p = vec3(0.0, -50.0, 0.0);       // degenerate: no area
    vUv = uv;
    vFog = fogAt(p);
    // the very tall towers light up at the peak
    vLit = smoothstep(0.90, 1.0, level) * step(2.4, k.y);   // only at the peak, only the tall ones
    vWin = uWindows * aSideFace * step(hsh(b, 151.0), 0.40);
    vCells = vec2(4.0, max(1.0, floor((y1 - y0) * H * 7.0)));
    vSeed = hsh(b, 199.0);
    gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
  }
`;
const towerFragment = `
  precision highp float;
  uniform vec3 uAccent;
  uniform float uPulse;
  varying vec2 vUv;
  varying float vFog;
  varying float vLit;
  varying float vWin;
  varying vec2 vCells;
  varying float vSeed;
  void main() {
    // px to the nearest edge of the face (R1): one half of the stroke per face
    vec2 fw = max(fwidth(vUv), vec2(1e-5));
    vec2 e = min(vUv, 1.0 - vUv) / fw;
    float d = min(e.x, e.y);
    // the core: 2 px across the edge (1 per face) and only just over the bloom
    // threshold - a thinner, brighter slanted core beads in the bloom's mips
    float core = (1.0 - smoothstep(0.55, 1.15, d)) * 0.88;
    float sheath = 1.0 - smoothstep(2.2, 3.4, d);
    float halo = exp(-d / 3.0) * 0.08;
    // a face only a few px across is ALL edge: its strokes merge into a white
    // mush the bloom turns into a grey veil - they fade with the face's size
    float facePx = 1.0 / max(fw.x, fw.y);
    float small = smoothstep(4.0, 16.0, facePx);
    // the beat's +10 % goes to the sheath and the halo: the core stays under
    // the bloom threshold (0.95), or slanted edges bead in the bloom's mips
    // the core REPLACES the sheath (a sum went over 1 and bloomed)
    vec3 glow = (uAccent * 0.7 * sheath + uAccent * halo) * (1.0 + 0.10 * uPulse);
    vec3 col = mix(glow, vec3(1.0), core * small) * (0.35 + 0.65 * small);
    // windows: a fine grid, very faint (10 %), fading out before it can alias
    if (vWin > 0.5) {
      vec2 g = vUv * vCells;
      vec2 cell = floor(g), f = fract(g);
      vec2 cpx = 1.0 / max(fwidth(g), vec2(1e-5));          // cell size in px
      float lod = smoothstep(3.0, 7.0, min(cpx.x, cpx.y));
      vec2 aa = fwidth(g) * 0.8;
      vec2 w = smoothstep(vec2(0.28) - aa, vec2(0.28) + aa, f) * (1.0 - smoothstep(vec2(0.72) - aa, vec2(0.72) + aa, f));
      vec3 p3 = fract(vec3(cell.xyx + vSeed * 17.0) * 0.1031);
      p3 += dot(p3, p3.yzx + 33.33);
      float on = step(0.45, fract((p3.x + p3.y) * p3.z));      // Hoskins hash12, no sin
      col += uAccent * 0.025 * w.x * w.y * on * lod;      // 10 % perceived: 0.025 linear
    }
    col += uAccent * 0.25 * vLit;
    gl_FragColor = vec4(col * vFog, 1.0);
  }
`;

// the crest: a neon square floating above the tower, coming down slowly
const crestVertex = `
  precision highp float;
  attribute vec2 aInst;
  ${BLOCK_GLSL}
  varying vec2 vUv;
  varying float vFog;
  void main() {
    vec2 b = windowBlock(aInst);
    vec4 k = blockOf(b);
    vec2 lv = levelsOf(b);
    float crest = max(lv.y, uSurge);
    float H = k.y * (0.4 + 1.2 * crest) * uFlat + 0.06;
    float hw = (k.z > 2.5 ? ${HALF[2].toFixed(2)} : (k.z > 1.5 ? ${HALF[1].toFixed(2)} : ${HALF[0].toFixed(2)})) + 0.03;
    vec2 rel = b - uOrigin;
    vec3 p = vec3(rel.x + 0.5 + position.x * hw, H, rel.y + 0.5 + position.y * hw);
    if (k.x > 0.5) p = vec3(0.0, -50.0, 0.0);
    vUv = uv;
    vFog = fogAt(p);
    gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
  }
`;
const crestFragment = `
  precision highp float;
  uniform vec3 uAccent;
  varying vec2 vUv;
  varying float vFog;
  void main() {
    vec2 fw = max(fwidth(vUv), vec2(1e-5));
    vec2 e = min(vUv, 1.0 - vUv) / fw;
    float d = min(e.x, e.y);
    if (d > 4.0) discard;
    float core = 1.0 - smoothstep(0.5, 1.2, d);
    float sheath = 1.0 - smoothstep(2.0, 3.6, d);
    vec3 col = mix(uAccent * 0.85 * sheath, vec3(0.92), core);      // brighter than the towers' edges, under the bloom threshold
    gl_FragColor = vec4(col * vFog, 1.0);
  }
`;

// the ground: the avenues' centre lines, fine, fading to their mean coverage
// before they can alias (Outrun's method), black fog
// the fog per PIXEL: the plane's four corners all lie outside the window
const groundVertex = `
  precision highp float;
  varying vec3 vP;
  void main() {
    vec4 w = modelMatrix * vec4(position, 1.0);
    vP = w.xyz;
    gl_Position = projectionMatrix * viewMatrix * w;
  }
`;
const groundFragment = `
  precision highp float;
  uniform vec3 uAccent;
  uniform float uN;
  uniform vec2 uWinCentre;
  uniform vec2 uFog;
  varying vec3 vP;
  float fogAt(vec3 p) {
    float dw = max(abs(p.x - uWinCentre.x), abs(p.z - uWinCentre.y)) / (uN * 0.5);
    float fw = 1.0 - smoothstep(0.68, 0.95, dw);
    float fc = 1.0 - smoothstep(uFog.x, uFog.y, length(p - cameraPosition));
    return fw * fc;
  }
  float gridLine(float g) {
    float fw = max(fwidth(g), 1e-5);
    float d = abs(fract(g + 0.5) - 0.5) / fw;            // px to the line
    float line = 1.0 - smoothstep(0.5, 1.4, d);
    float cellPx = 1.0 / fw;
    float mean = min(1.0, 1.6 / cellPx);
    return mix(mean, line, smoothstep(2.0, 6.0, cellPx));
  }
  void main() {
    float l = max(gridLine(vP.x), gridLine(vP.z));
    gl_FragColor = vec4(uAccent * 0.32 * l * fogAt(vP), 1.0);
  }
`;

// the light streams: one instanced quad per flying car, from its tail to its
// head, widened in SCREEN space (a fat line, R1: core, sheath, halo in px)
const carVertex = `
  precision highp float;
  attribute vec2 aCorner;         // x: 0 tail / 1 head, y: -1 / +1 across
  attribute vec4 aCarA;           // axis (0: runs along z, 1: along x), avenue slot, direction, phase
  attribute vec4 aCarB;           // height, speed, length, white (1) / red (0)
  ${BLOCK_GLSL}
  uniform float uCarTravel;
  uniform vec2 uRes;
  uniform float uPx;
  varying float vAcross;
  varying float vAlong;
  varying float vFog;
  varying float vWhite;
  void main() {
    float axis = aCarA.x, dir = aCarA.z;
    // the avenue: absolute, recycled like the blocks
    float baseCross = axis < 0.5 ? uWinBase.x : uWinBase.y;
    float baseAlong = axis < 0.5 ? uWinBase.y : uWinBase.x;
    float origCross = axis < 0.5 ? uOrigin.x : uOrigin.y;
    float origAlong = axis < 0.5 ? uOrigin.y : uOrigin.x;
    float av = baseCross + imod(aCarA.y - baseCross, uN) - origCross;
    float lane = av + dir * 0.11;                    // right-hand traffic, clear of the towers (0.22)
    float sAbs = aCarA.w * uN + dir * uCarTravel * aCarB.y;
    float head = (baseAlong - origAlong) + mod(sAbs - baseAlong, uN);
    float tail = head - dir * aCarB.z;
    vec3 ph = axis < 0.5 ? vec3(lane, aCarB.x, head) : vec3(head, aCarB.x, lane);
    vec3 pt = axis < 0.5 ? vec3(lane, aCarB.x, tail) : vec3(tail, aCarB.x, lane);
    vFog = fogAt(ph);
    vec4 v0 = viewMatrix * vec4(pt, 1.0), v1 = viewMatrix * vec4(ph, 1.0);
    float nz = -0.03;
    if (v0.z > nz && v1.z > nz) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vAcross = 0.0; vAlong = 0.0; vWhite = 0.0; return; }
    if (v0.z > nz) v0 = mix(v0, v1, (nz - v0.z) / (v1.z - v0.z));
    if (v1.z > nz) v1 = mix(v1, v0, (nz - v1.z) / (v0.z - v1.z));
    vec4 c0 = projectionMatrix * v0, c1 = projectionMatrix * v1;
    vec2 s0 = c0.xy / c0.w * uRes * 0.5, s1 = c1.xy / c1.w * uRes * 0.5;
    vec2 dv = s1 - s0;
    float len = length(dv);
    vec2 dirS = len > 1e-3 ? dv / len : vec2(1.0, 0.0);
    vec2 perp = vec2(-dirS.y, dirS.x);
    float hp = 5.0 * uPx;                            // the quad's half width
    vec4 c = aCorner.x < 0.5 ? c0 : c1;
    vec2 s = (aCorner.x < 0.5 ? s0 - dirS * hp : s1 + dirS * hp) + perp * aCorner.y * hp;
    gl_Position = vec4(s / (uRes * 0.5) * c.w, c.z, c.w);
    vAcross = aCorner.y * hp / uPx;
    vAlong = aCorner.x;
    vWhite = aCarB.w;
  }
`;
const carFragment = `
  precision highp float;
  varying float vAcross;
  varying float vAlong;
  varying float vFog;
  varying float vWhite;
  void main() {
    float d = abs(vAcross);
    float core = 1.0 - smoothstep(0.4, 0.95, d);
    float sheath = 1.0 - smoothstep(1.8, 3.0, d);
    float halo = exp(-d / 2.5) * 0.30;
    vec3 c = vWhite > 0.5 ? vec3(1.0, 0.93, 0.82) : vec3(1.0, 0.10, 0.06);
    float trail = vAlong * vAlong;                   // the head bright, the tail fading
    vec3 col = (vec3(1.0) * core * (0.4 + 0.6 * vWhite) + c * 0.75 * sheath + c * halo) * trail;
    gl_FragColor = vec4(col * vFog, 1.0);
  }
`;

// the giant screen: the cover on a tower's face, an illuminated sign
const signVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;
const signFragment = `
  precision highp float;
  uniform sampler2D uCover;
  uniform float uHasCover;
  uniform vec3 uAccent;
  uniform float uSignFog;
  varying vec2 vUv;
  void main() {
    vec2 fw = max(fwidth(vUv), vec2(1e-5));
    vec2 e = min(vUv, 1.0 - vUv) / fw;
    float d = min(e.x, e.y);
    vec3 cov = texture2D(uCover, vUv).rgb;
    vec3 col = cov * 0.85 * uHasCover;
    col = mix(col, vec3(1.0), 1.0 - smoothstep(0.5, 1.2, d));
    col += uAccent * 0.7 * (1.0 - smoothstep(1.8, 3.2, d));
    gl_FragColor = vec4(col * uSignFog, 1.0);
  }
`;

const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const quadFragment = `
  precision highp float;
  uniform sampler2D uScene;
  uniform float uMirror;
  uniform float uAspect;
  uniform float uGain;
  varying vec2 vUv;
  ${VDJ_MIRROR_CHUNK}
  void main() {
    vec2 uv = vdjMirror(vUv, uMirror, uAspect);
    gl_FragColor = vec4(texture2D(uScene, uv).rgb * uGain, 1.0);
  }
`;

// --- geometry -------------------------------------------------------------------
// three stacked unit boxes (x, z in [-1, 1], y in [0, 1]), four sides and a
// top each, UV 0..1 per face
function towerGeometry() {
  const pos = [], uv = [], tier = [], side = [], idx = [];
  const face = (a, b, c, d, t, s) => {
    const o = pos.length / 3;
    for (const p of [a, b, c, d]) pos.push(p[0], p[1], p[2]);
    uv.push(0, 0, 1, 0, 1, 1, 0, 1);
    for (let i = 0; i < 4; i++) { tier.push(t); side.push(s); }
    idx.push(o, o + 1, o + 2, o, o + 2, o + 3);
  };
  for (let t = 0; t < 3; t++) {
    face([-1, 0, 1], [1, 0, 1], [1, 1, 1], [-1, 1, 1], t, 1);       // +z
    face([1, 0, -1], [-1, 0, -1], [-1, 1, -1], [1, 1, -1], t, 1);   // -z
    face([1, 0, 1], [1, 0, -1], [1, 1, -1], [1, 1, 1], t, 1);       // +x
    face([-1, 0, -1], [-1, 0, 1], [-1, 1, 1], [-1, 1, -1], t, 1);   // -x
    face([-1, 1, 1], [1, 1, 1], [1, 1, -1], [-1, 1, -1], t, 0);     // top
  }
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('aTier', new THREE.Float32BufferAttribute(tier, 1));
  g.setAttribute('aSideFace', new THREE.Float32BufferAttribute(side, 1));
  g.setIndex(idx);
  return g;
}
function instGrid(g, n) {
  const a = new Float32Array(n * n * 2);
  for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) { a[(j * n + i) * 2] = i; a[(j * n + i) * 2 + 1] = j; }
  g.setAttribute('aInst', new THREE.InstancedBufferAttribute(a, 2));
  g.instanceCount = n * n;
  return g;
}

const MATS = new Map();
let UNI = null;
let BLANK = null;

export class CyberflightVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const shot = urlParam('cybershot');
    this._forcedShot = SHOT_NAMES.indexOf(shot) >= 0 ? shot : null;
    const eq = parseFloat(urlParam('cybereq'));
    this._forcedEq = Number.isFinite(eq) ? clamp01(eq) : null;
    const mi = urlParam('cybermirror');
    this._forcedMirror = mi && VDJ_MIRROR_NAMES[mi] != null ? VDJ_MIRROR_NAMES[mi] : null;
    this._post = new VdjPost({ bloomStrength: 0.30, bloomThreshold: 0.95, vignette: 0.30, grain: 0.010, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    const L = this._layout, N = L.n;
    this._n = N;
    this._inner = new THREE.Scene();
    this._cam = new THREE.PerspectiveCamera(62, 16 / 9, 0.02, 80);
    // shared uniforms: ONE module-level object (materials are cached; three
    // keeps the uniform object a material was compiled with)
    if (!UNI) UNI = {
      uN: { value: N }, uWinBase: { value: new THREE.Vector2() }, uOrigin: { value: new THREE.Vector2() }, uWinCentre: { value: new THREE.Vector2() },
      uLevels: { value: null }, uFlat: { value: 1 }, uSurge: { value: 0 }, uFog: { value: new THREE.Vector2(8, 14) },
      uAccent: { value: new THREE.Color(0.2, 0.8, 1.0) }, uPulse: { value: 0 }, uWindows: { value: 1 },
      uCarTravel: { value: 0 }, uRes: { value: new THREE.Vector2(1280, 720) }, uPx: { value: 1 },
      uCover: { value: null }, uHasCover: { value: 0 }, uSignFog: { value: 1 }
    };
    const u = this._u = UNI;
    u.uN.value = N; u.uWindows.value = L.windows ? 1 : 0;
    // the levels: 16 x 1, r level, g crest
    this._levelsData = new Uint8Array(BANDS * 4);
    this._levelsTex = new THREE.DataTexture(this._levelsData, BANDS, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
    this._levelsTex.minFilter = this._levelsTex.magFilter = THREE.NearestFilter;
    this._levelsTex.generateMipmaps = false; this._levelsTex.needsUpdate = true;
    u.uLevels.value = this._levelsTex;
    if (!BLANK) { BLANK = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1); BLANK.needsUpdate = true; }
    u.uCover.value = BLANK; u.uHasCover.value = 0;

    const mat = (key, make) => { let m = MATS.get(key); if (!m) { m = make(); MATS.set(key, m); } return m; };
    // ground
    this._ground = new THREE.Mesh(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2), mat('ground', () => new THREE.ShaderMaterial({
      uniforms: u, vertexShader: groundVertex, fragmentShader: groundFragment })));
    this._ground.scale.set(N * 1.1, 1, N * 1.1);
    this._ground.frustumCulled = false; this._ground.renderOrder = 0;
    this._inner.add(this._ground);
    // towers
    this._towerGeo = instGrid(towerGeometry(), N);
    this._towers = new THREE.Mesh(this._towerGeo, mat('tower', () => new THREE.ShaderMaterial({
      uniforms: u, vertexShader: towerVertex, fragmentShader: towerFragment })));
    this._towers.frustumCulled = false; this._towers.renderOrder = 1;
    this._inner.add(this._towers);
    // crests
    const cq = new THREE.InstancedBufferGeometry();
    cq.setAttribute('position', new THREE.Float32BufferAttribute([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0], 3));
    cq.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
    cq.setIndex([0, 1, 2, 0, 2, 3]);
    this._crestGeo = instGrid(cq, N);
    this._crests = new THREE.Mesh(this._crestGeo, mat('crest', () => new THREE.ShaderMaterial({
      uniforms: u, vertexShader: crestVertex, fragmentShader: crestFragment, side: THREE.DoubleSide,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending })));
    this._crests.frustumCulled = false; this._crests.renderOrder = 3;
    this._inner.add(this._crests);
    // cars
    let seed = 987654321;
    const rnd = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
    const cg = new THREE.InstancedBufferGeometry();
    cg.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], 3));
    cg.setAttribute('aCorner', new THREE.Float32BufferAttribute([0, -1, 1, -1, 1, 1, 0, 1], 2));
    cg.setIndex([0, 1, 2, 0, 2, 3]);
    const nc = L.cars, A = new Float32Array(nc * 4), B = new Float32Array(nc * 4);
    for (let i = 0; i < nc; i++) {
      const axis = rnd() < 0.62 ? 0 : 1, dir = rnd() < 0.5 ? -1 : 1;
      A[i * 4] = axis; A[i * 4 + 1] = Math.floor(rnd() * N); A[i * 4 + 2] = dir; A[i * 4 + 3] = rnd();
      B[i * 4] = 0.10 + 0.55 * rnd(); B[i * 4 + 1] = 0.9 + 1.4 * rnd(); B[i * 4 + 2] = 0.8 + 1.4 * rnd();
      // along z: oncoming (dir +1, toward the camera flying to -z) are headlights
      B[i * 4 + 3] = axis === 0 ? (dir > 0 ? 1 : 0) : (rnd() < 0.5 ? 1 : 0);
    }
    cg.setAttribute('aCarA', new THREE.InstancedBufferAttribute(A, 4));
    cg.setAttribute('aCarB', new THREE.InstancedBufferAttribute(B, 4));
    cg.instanceCount = nc;
    this._carGeo = cg;
    this._cars = new THREE.Mesh(cg, mat('car', () => new THREE.ShaderMaterial({
      uniforms: u, vertexShader: carVertex, fragmentShader: carFragment,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending })));
    this._cars.frustumCulled = false; this._cars.renderOrder = 4;
    this._inner.add(this._cars);
    // the sign
    this._sign = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat('sign', () => new THREE.ShaderMaterial({
      uniforms: u, vertexShader: signVertex, fragmentShader: signFragment, side: THREE.DoubleSide })));
    this._sign.renderOrder = 2; this._sign.visible = false;
    this._inner.add(this._sign);

    // the private target and the quad that shows it (mirror, gain)
    this._rt = new THREE.WebGLRenderTarget(4, 4, { samples: L.samples, type: THREE.UnsignedByteType, colorSpace: THREE.SRGBColorSpace, depthBuffer: true, stencilBuffer: false });
    this._qu = { uScene: { value: this._rt.texture }, uMirror: { value: 0 }, uAspect: { value: 16 / 9 }, uGain: { value: 1 } };
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._qu, vertexShader: quadVertex, fragmentShader: quadFragment, depthTest: false, depthWrite: false }));
    this._quad.frustumCulled = false; this._quad.renderOrder = -3000;
    const self = this;
    this._size = new THREE.Vector2();
    this._quad.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._quad);

    // state
    this._time = 0; this._beats = 0; this._lastBeats = null; this._flight = 0; this._carTravel = 0;
    this._lastBeatPhase = null; this._lastBeatIndex = null; this._lastBarPhase = null; this._lastPhrasePhase = null;
    this._beatCount = 0; this._barCount = 0; this._phraseCount = 0; this._beatPeriod = 60 / SYNTH_BPM;
    this._level = new Float32Array(BANDS); this._peak = new Float32Array(BANDS); this._peakHold = new Float32Array(BANDS); this._target = new Float32Array(BANDS);
    this._noisePhase = new Float32Array(BANDS); this._noiseRate = new Float32Array(BANDS);
    for (let b = 0; b < BANDS; b++) { this._noisePhase[b] = rnd() * 6.28; this._noiseRate[b] = 0.4 + 0.9 * rnd(); }
    this._kick = 0; this._pulse = 0; this._rate = 1; this._flat = 1; this._surge = 0; this._dropAt = -99; this._dropShotUntil = -1;
    this._palette = [0x2bd6ff, 0xff2bd6, 0x8a2bff, 0x2bff9e].map((c) => vdjColor(c)); this._colorIndex = 0; this._lastPaletteRef = undefined;
    this._mirror = this._forcedMirror != null ? this._forcedMirror : 0;
    this._shotIndex = 0; this._shot = null;
    this._camAbs = new THREE.Vector3(0.0, 0.17, 0.0);
    this._avenueX = 0;
    this._minClear = Infinity;
    this._kickCam = { zoom: 1, roll: 0 };
    this._coverUrl = undefined; this._coverTex = null;
    this._setAccent();
    this._cut(this._forcedShot || 'avenue');
  }

  // --- the city, on the CPU (the same hash as the GPU) ---------------------------
  _top(bx, bz) { const p = blockProps(bx, bz); return p.empty ? 0 : p.base * HEIGHT_MAX; }
  _maxTop(x0, x1, z0, z1) {
    let m = 0;
    for (let bz = Math.floor(z0); bz <= Math.floor(z1); bz++) for (let bx = Math.floor(x0); bx <= Math.floor(x1); bx++) m = Math.max(m, this._top(bx, bz));
    return m;
  }

  // --- shots ----------------------------------------------------------------------
  _cut(name) {
    this._shot = name;
    this._shotStart = this._time;
    this._shotBeats0 = this._beats;
    // the FLIGHT line, not the last camera position (an orbit's camera is
    // around its tower, far from where the flight resumes)
    const z = -this._flight;
    const x = this._avenueX;
    // the path the bar covers at the current rate (+2 blocks of margin); only
    // the column the camera flies over can be hit (the neighbours' faces stay
    // 0.22 from the avenues the camera never leaves)
    const z0 = z - (4 * this._rate + 2), z1 = z + 1;
    if (name === 'avenue') {
      // the nearest avenue that has a tower to carry the giant screen
      this._avenueX = x;
      for (const a of [x, x + 1, x - 1, x + 2, x - 2]) { if (this._signSpot(a, z)) { this._avenueX = a; break; } }
      this._placeSign('avenue');
    } else if (name === 'rooftop' || name === 'bank') {
      // the column with the lowest roofs nearby
      let best = null;
      for (let a = x - 3; a <= x + 3; a++) {
        const m = this._maxTop(a, a, z0, z1);
        if (!best || m < best.m) best = { a, m };
      }
      this._avenueX = best.a;
      this._shotY = Math.max(1.0, best.m + CAM_CLEAR);
      this._bankSign = (this._barCount >> 1) % 2 === 0 ? 1 : -1;
      this._sign.visible = false;
    } else if (name === 'topdown') {
      this._avenueX = x;
      this._sign.visible = false;
    } else if (name === 'orbit') {
      // the tallest tower of the neighbourhood ahead
      let g = null;
      for (let bz = Math.floor(z - 14); bz <= Math.floor(z - 3); bz++) for (let bx = x - 5; bx <= x + 5; bx++) {
        const p = blockProps(bx, bz); if (p.empty) continue;
        if (!g || p.base > g.base) g = { bx, bz, base: p.base };
      }
      this._giant = g || { bx: x, bz: Math.floor(z - 6), base: 2 };
      const cx = this._giant.bx + 0.5, cz = this._giant.bz + 0.5;
      // only the blocks whose footprint the orbit's circle can cross
      let m = 0;
      const R = ORBIT_R + 1;
      for (let bz = Math.floor(cz - R); bz <= Math.floor(cz + R); bz++) for (let bx = Math.floor(cx - R); bx <= Math.floor(cx + R); bx++) {
        if (bx === this._giant.bx && bz === this._giant.bz) continue;
        const dc = Math.hypot(bx + 0.5 - cx, bz + 0.5 - cz);
        if (Math.abs(dc - ORBIT_R) > HALF[0] * Math.SQRT2 + 0.05) continue;
        m = Math.max(m, this._top(bx, bz));
      }
      this._shotY = Math.max(0.6, m + CAM_CLEAR);
      this._orbit0 = vdjLoop(this._beats, 16) * Math.PI * 2;
      this._placeSign('orbit');
    }
  }

  _nextShot() {
    if (this._forcedShot) return this._forcedShot;
    this._shotIndex = (this._shotIndex + 1) % SHOT_CYCLE.length;
    let s = SHOT_CYCLE[this._shotIndex];
    if (s === this._shot) { this._shotIndex = (this._shotIndex + 1) % SHOT_CYCLE.length; s = SHOT_CYCLE[this._shotIndex]; }
    return s;
  }

  // the giant screen: on the avenue side of a tall, straight tower ahead, or
  // on the orbit tower's front face
  _placeSign(kind) {
    const s = this._sign;
    s.visible = false;
    if (kind === 'avenue') {
      const spot = this._signSpot(this._avenueX, -this._flight);
      if (spot) {
        this._signAbs = spot.pos; this._signRotY = spot.rotY;
        s.scale.set(SIGN_SIZE, SIGN_SIZE, 1);
        s.visible = true;
      }
    } else if (kind === 'orbit' && this._giant) {
      const g = this._giant;
      this._signAbs = new THREE.Vector3(g.bx + 0.5, 0.15 + SIGN_SIZE / 2, g.bz + 0.5 + HALF[0] + 0.004);
      this._signRotY = 0;
      s.scale.set(SIGN_SIZE, SIGN_SIZE, 1);
      s.visible = true;
    }
  }

  // a straight tower (one tier, base >= 1.6: at least 0.64 tall at level 0,
  // the screen's top is at 0.6) 4 to 11 blocks ahead, on either side of the
  // avenue; the screen on its face toward the avenue
  _signSpot(a, z) {
    for (let bz = Math.floor(z) - 4; bz >= Math.floor(z) - 11; bz--) {
      for (const side of [1, -1]) {
        const bx = side > 0 ? a : a - 1;
        const p = blockProps(bx, bz);
        if (p.empty || p.tiers !== 1 || p.base < 1.6) continue;
        return { pos: new THREE.Vector3(side > 0 ? a + 0.5 - HALF[0] - 0.004 : a - 0.5 + HALF[0] + 0.004, 0.15 + SIGN_SIZE / 2, bz + 0.5), rotY: side > 0 ? Math.PI / 2 : -Math.PI / 2 };
      }
    }
    return null;
  }

  // the camera's pose for the current shot, in ABSOLUTE coordinates
  _pose(dt) {
    const cam = this._cam, a = this._camAbs, N = this._n;
    const fwd = new THREE.Vector3(0, 0, -1);
    let look, up = new THREE.Vector3(0, 1, 0), roll = 0, fov = 62, fogNear = 0.30 * N, fogFar = 0.58 * N;
    let centre;
    const z = -this._flight;
    if (this._shot === 'avenue') {
      a.set(this._avenueX, 0.17, z);
      look = new THREE.Vector3(this._avenueX, 0.75, z - 6);
      centre = new THREE.Vector2(a.x, a.z - 0.22 * N);
    } else if (this._shot === 'rooftop') {
      a.set(this._avenueX + 0.5, this._shotY, z);
      look = new THREE.Vector3(a.x, this._shotY - 1.6, z - 5);
      centre = new THREE.Vector2(a.x, a.z - 0.22 * N);
    } else if (this._shot === 'bank') {
      const sway = Math.sin(vdjLoop(this._beats, 8) * Math.PI * 2) * 0.5;
      a.set(this._avenueX + 0.5 + sway, this._shotY + 0.05, z);
      look = new THREE.Vector3(a.x + 0.9 * this._bankSign, this._shotY - 1.3, z - 5);
      roll = BANK_ROLL * this._bankSign;
      centre = new THREE.Vector2(a.x, a.z - 0.22 * N);
    } else if (this._shot === 'topdown') {
      a.set(this._avenueX + 0.5, TOPDOWN_Y, z);
      look = new THREE.Vector3(a.x, 0, z - 0.001);
      up = new THREE.Vector3(0, 0, -1);
      fov = 58; fogNear = 1e3; fogFar = 2e3;
      centre = new THREE.Vector2(a.x, a.z - 0.5);
    } else {
      const g = this._giant, cx = g.bx + 0.5, cz = g.bz + 0.5;
      const ang = this._orbit0 + (this._beats - this._shotBeats0) / 16 * Math.PI * 2;
      a.set(cx + Math.sin(ang) * ORBIT_R, this._shotY, cz + Math.cos(ang) * ORBIT_R);
      look = new THREE.Vector3(cx, Math.min(this._shotY * 0.8, g.base * 0.7), cz);
      fov = 56; fogNear = 0.35 * N; fogFar = 0.6 * N;
      centre = new THREE.Vector2(cx, cz);
    }
    // the relative frame: an integer origin near the camera
    const ox = Math.floor(a.x), oz = Math.floor(a.z);
    const u = this._u;
    u.uOrigin.value.set(ox, oz);
    const wb = new THREE.Vector2(Math.floor(centre.x) - N / 2, Math.floor(centre.y) - N / 2);
    u.uWinBase.value.copy(wb);
    u.uWinCentre.value.set(wb.x + N / 2 - ox, wb.y + N / 2 - oz);
    u.uFog.value.set(fogNear, fogFar);
    cam.position.set(a.x - ox, a.y, a.z - oz);
    cam.up.copy(up);
    cam.lookAt(look.x - ox, look.y, look.z - oz);
    cam.rotateZ(roll + this._kickCam.roll);
    cam.fov = fov;
    cam.zoom = this._kickCam.zoom;
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld();
    this._ground.position.set(u.uWinCentre.value.x, 0, u.uWinCentre.value.y);
    if (this._sign.visible && this._signAbs) {
      this._sign.position.set(this._signAbs.x - ox, this._signAbs.y, this._signAbs.z - oz);
      this._sign.rotation.set(0, this._signRotY, 0);
      const d = this._sign.position.distanceTo(cam.position);
      u.uSignFog.value = (1 - smoothstep(fogNear, fogFar, d)) * clamp01((this._flat - 0.85) / 0.15);
    }
    this._checkClearance();
  }

  // for the probe: the camera's distance to the nearest tower box (with the
  // CURRENT heights); negative = inside a tower
  _checkClearance() {
    const a = this._camAbs;
    let best = Infinity;
    for (let bz = Math.floor(a.z) - 1; bz <= Math.floor(a.z) + 1; bz++) for (let bx = Math.floor(a.x) - 1; bx <= Math.floor(a.x) + 1; bx++) {
      const p = blockProps(bx, bz); if (p.empty) continue;
      const lv = this._shownLevel(bx);
      const H = p.base * (0.4 + 1.2 * lv) * this._flat;
      const dx = Math.max(0, Math.abs(a.x - (bx + 0.5)) - HALF[0]), dz = Math.max(0, Math.abs(a.z - (bz + 0.5)) - HALF[0]), dy = Math.max(0, a.y - H);
      let d = Math.hypot(dx, dy, dz);
      if (dx === 0 && dz === 0 && dy === 0) d = -1;
      best = Math.min(best, d);
    }
    this._clearance = best;
    if (best < this._minClear) this._minClear = best;
  }
  _shownLevel(bx) {
    const band = ((bx % BANDS) + BANDS) % BANDS;
    return Math.max(this._forcedEq != null ? this._forcedEq : this._level[band], this._surge);
  }

  _setAccent() {
    vdjColor(this._palette[this._colorIndex % this._palette.length], this._u.uAccent.value, 1.0);
  }
  _applyPalette(pal) {
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : null;
    if (v) this._palette = v.slice(0, 4).map((c) => vdjColor(c));
    this._setAccent();
  }
  _loadCover(profile) {
    if (profile.artworkUrl === this._coverUrl) return;
    this._coverUrl = profile.artworkUrl;
    this._u.uCover.value = BLANK; this._u.uHasCover.value = 0;
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    if (!profile.artworkUrl) return;
    const self = this, url = profile.artworkUrl;
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, (tex) => {
      if (!self._scene || url !== self._coverUrl) { tex.dispose(); return; }
      self._coverTex = tex; self._u.uCover.value = tex; self._u.uHasCover.value = 1;
    }, () => {});
  }

  onTrackChange(profile) { this._loadCover(profile); }

  // --- rhythm ---------------------------------------------------------------------
  _onBeat() {
    this._beatCount++;
    this._kick = 1;
    this._pulse = 1;
  }
  _onBar() {
    this._barCount++;
    if (this._time < this._dropShotUntil) return;
    if (this._dropShotUntil > 0) { this._dropShotUntil = -1; this._cut(this._forcedShot || 'avenue'); return; }
    this._cut(this._nextShot());
  }
  _onPhrase() {
    this._phraseCount++;
    this._colorIndex++;
    this._setAccent();
    if (this._forcedMirror == null) this._mirror = this._phraseCount % MIRROR_EVERY_PHRASES === 0 ? (this._phraseCount % (2 * MIRROR_EVERY_PHRASES) === 0 ? 2 : 1) : 0;
  }
  _onDrop(director) {
    this._dropAt = this._time;
    if (this._comfort.requestFlash(this._time) && director) director.flash = Math.max(director.flash || 0, VDJ_DROP_FLASH);
    this._dropShotUntil = this._time + 8 * this._beatPeriod - 0.05;      // two bars of TOPDOWN
    this._cut(this._forcedShot || 'topdown');
  }

  // --- the equaliser --------------------------------------------------------------
  _equaliser(bands, energy, dt) {
    const bv = bands ? [bands.low, bands.mid, bands.high] : null;
    for (let b = 0; b < BANDS; b++) {
      const x = b / (BANDS - 1);
      let v;
      if (bv) {
        let ws = 0; v = 0;
        for (let i = 0; i < 3; i++) { const d = (x - BAND_CENTRES[i]) / BAND_SIGMA; const w = Math.exp(-d * d); v += w * bv[i]; ws += w; }
        v = ws > 0 ? v / ws : 0;
      } else {
        // no analysis: the energy, shaped like a spectrum (bass higher)
        v = energy * (0.58 - 0.28 * x);
      }
      this._noisePhase[b] += dt * this._noiseRate[b];
      v *= 1 + DECORR * Math.sin(this._noisePhase[b]) + (bv ? 0 : 0.25 * Math.sin(this._noisePhase[b] * 1.7 + b));
      v += KICK_SPIKE * this._kick * (1 - 0.6 * x);
      this._target[b] = clamp01(v);
    }
    this._kick = Math.max(0, this._kick - dt * 8);
    for (let b = 0; b < BANDS; b++) {
      const t = this._target[b];
      let v = this._level[b];
      if (t >= v) v = t; else v = Math.max(t, v - FALL_PER_S * dt);
      this._level[b] = v;
      if (v >= this._peak[b]) { this._peak[b] = v; this._peakHold[b] = PEAK_HOLD_S; }
      else if (this._peakHold[b] > 0) this._peakHold[b] -= dt;
      else this._peak[b] = Math.max(v, this._peak[b] - PEAK_FALL_PER_S * dt);
    }
    const d = this._levelsData;
    for (let b = 0; b < BANDS; b++) {
      const lv = this._forcedEq != null ? this._forcedEq : this._level[b];
      const pk = this._forcedEq != null ? this._forcedEq : this._peak[b];
      d[b * 4] = Math.round(clamp01(lv) * 255); d[b * 4 + 1] = Math.round(clamp01(pk) * 255);
    }
    this._levelsTex.needsUpdate = true;
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    const dt = Math.min(deltaTime, 0.05);
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    this._beatPeriod = 60 / bpm;
    if (profile.artworkPalette !== this._lastPaletteRef) { this._lastPaletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    this._loadCover(profile);
    if (paused) return;            // the frame holds; the last pose keeps rendering
    this._time += dt;
    const bands = director && director.hasReliableGrid && director.bands ? director.bands : null;
    const energy = director && director.intensity != null ? clamp01(director.intensity) : 0.65;

    // --- the beat clock: the grid's own index + phase when there is one
    const beatPhase = director && director.beat != null ? director.beat : profile.beatPhase;
    let beatEdge = false;
    if (beatPhase != null) {
      if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) beatEdge = true;
      const bi = profile.beatIndex;
      if (bi != null && this._lastBeatIndex != null) this._beats += Math.max(0, (bi - this._lastBeatIndex) + (beatPhase - this._lastBeatPhase));
      else this._beats += dt / this._beatPeriod;
      this._lastBeatIndex = bi != null ? bi : null;
      this._lastBeatPhase = beatPhase;
    } else {
      const before = Math.floor(this._beats);
      this._beats += dt / this._beatPeriod;
      if (Math.floor(this._beats) !== before) beatEdge = true;
    }
    // the montee: x2 then x4, the towers come down to a flat grid
    const ev = director && director.nextEvent;
    const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
    const beatsToDrop = dropMs != null && dropMs > 0 ? dropMs / 1000 / this._beatPeriod : null;
    const rate = beatsToDrop != null && beatsToDrop < 32 ? (beatsToDrop < 12 ? 4 : 2) : 1;
    this._rate = rate;
    const flatTarget = beatsToDrop != null && beatsToDrop < 32 ? 1 - 0.95 * clamp01((32 - beatsToDrop) / 24) : 1;
    this._flat += (flatTarget - this._flat) * Math.min(1, dt / 0.25);
    const dBeats = Math.max(0, this._beats - (this._lastBeats != null ? this._lastBeats : this._beats));
    this._lastBeats = this._beats;
    this._flight += dBeats * rate;                       // one block a beat
    this._carTravel += dBeats * Math.max(1, rate);
    if (beatEdge) this._onBeat();
    const barPhase = director && director.bar != null ? director.bar : profile.barPhase;
    if (barPhase != null) { if (this._lastBarPhase != null && barPhase < this._lastBarPhase) this._onBar(); this._lastBarPhase = barPhase; }
    else if (beatEdge && this._beatCount % 4 === 1 && this._beatCount > 1) this._onBar();
    const phrasePhase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
    if (phrasePhase != null) { if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) this._onPhrase(); this._lastPhrasePhase = phrasePhase; }
    else if (beatEdge && this._beatCount % 32 === 1 && this._beatCount > 1) this._onPhrase();
    // the drop, on the frame that crosses the mark
    const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (dropMs != null && dropMs <= dt * 500 + 30 && nowMs != null) {
      const key = Math.round((nowMs + dropMs) / 100);
      if (key !== this._lastDropKey) { this._lastDropKey = key; this._flat = 1; this._onDrop(director); }
    }
    // the surge: every tower to its maximum in half a beat, held a beat, then
    // down over two beats (the ballistics take over)
    const sd = (this._time - this._dropAt) / this._beatPeriod;
    this._surge = sd < 0 ? 0 : sd < 0.5 ? sd / 0.5 : sd < 1.5 ? 1 : Math.max(0, 1 - (sd - 1.5) / 2);

    this._equaliser(bands, energy, dt);
    this._pulse = Math.max(0, this._pulse - dt / (this._beatPeriod * 0.35));
    const bp = barPhase != null ? barPhase : (this._beats % 4) / 4;
    this._kickCam = vdjCameraKick(bp, this._barCount);
    this._post.update(vdjBeatChroma(beatPhase != null ? beatPhase : this._beats % 1, this._beatPeriod));

    const u = this._u;
    u.uFlat.value = this._flat;
    u.uSurge.value = this._surge;
    u.uPulse.value = this._pulse;
    u.uCarTravel.value = this._carTravel * 1.0;
    this._qu.uMirror.value = this._mirror;
    // comfort: thin neon on black; the flash is the only full-screen light
    const est = 0.07 + (director && director.flash ? director.flash * 0.8 : 0);
    this._qu.uGain.value = this._comfort.measure(est, dt);
  }

  _render(renderer) {
    renderer.getDrawingBufferSize(this._size);
    if (this._rt.width !== this._size.x || this._rt.height !== this._size.y) this._rt.setSize(this._size.x, this._size.y);
    const aspect = this._size.x / Math.max(1, this._size.y);
    this._cam.aspect = aspect;
    this._qu.uAspect.value = aspect;
    this._u.uRes.value.copy(this._size);
    this._u.uPx.value = Math.max(1, this._size.y / 700);
    this._pose();
    const prev = renderer.getRenderTarget();
    const cc = new THREE.Color(); renderer.getClearColor(cc); const ca = renderer.getClearAlpha();
    renderer.setRenderTarget(this._rt);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, false);
    renderer.render(this._inner, this._cam);
    renderer.setClearColor(cc, ca);
    renderer.setRenderTarget(prev);
  }

  getProbe() {
    return {
      t: +this._time.toFixed(2), beats: +this._beats.toFixed(2), flight: +this._flight.toFixed(2), shot: this._shot, rate: this._rate,
      flat: +this._flat.toFixed(3), surge: +this._surge.toFixed(3), mirror: this._mirror,
      cam: [+this._camAbs.x.toFixed(2), +this._camAbs.y.toFixed(2), +this._camAbs.z.toFixed(2)],
      clearance: +(this._clearance || 0).toFixed(3), minClear: +this._minClear.toFixed(3),
      levels: Array.from(this._level).map((v) => +v.toFixed(2)), peaks: Array.from(this._peak).map((v) => +v.toFixed(2)),
      sign: this._sign.visible, gain: +this._comfort.gain.toFixed(3)
    };
  }

  dispose() {
    if (this._quad) { this._quad.onBeforeRender = null; if (this._scene) this._scene.remove(this._quad); this._quad.geometry.dispose(); this._quad.material.dispose(); }
    for (const m of [this._ground, this._towers, this._crests, this._cars, this._sign]) if (m) m.geometry.dispose();
    if (this._rt) this._rt.dispose();
    if (this._levelsTex) this._levelsTex.dispose();
    if (this._u) { this._u.uCover.value = BLANK; this._u.uHasCover.value = 0; }
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    this._scene = null;
  }
}

function smoothstep(a, b, x) { const t = clamp01((x - a) / (b - a)); return t * t * (3 - 2 * t); }
